import { useContext, useEffect, useState } from 'react';

import {
  DevHubAutocomplete,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubChip,
  DevHubModal,
  DevHubTextField,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useMutation } from '@apollo/client';
import { Submitted } from '../roles/Submitted';
import {
  ApplicationType,
  AssociateAccountType,
  CloudAccountType,
} from '../types/CloudAccountTypes';
import { CreateCloudAccountMutation } from './CloudAccountQueries';
import { SystemContext } from '../../../contexts/SystemContext';

type Props = {
  appId: number | null;
  buttonText: string;
  userAccounts: CloudAccountType[];
  disabled: boolean;
  applications?: ApplicationType[] | null;
  preSelectedAccounts?: CloudAccountType[];
  open: boolean;
  type: AssociateAccountType;
  handleOpen: React.MouseEventHandler<HTMLButtonElement>;
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  cloudAccountsList: CloudAccountType[];
  setCloudAccountsList: React.Dispatch<
    React.SetStateAction<CloudAccountType[]>
  >;
};

/**
 * Modal pop up for associating cloud accounts to an app
 * @param props - appId, cloudAccountsQuery, buttonText
 * @returns Modal pop up for associating cloud accounts to an app
 */
export function AssociateAccount(props: Props) {
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [_selectedAccountType, setSelectedAccountType] = useState<string>('');
  const [selectedCloudAccounts, setSelectedCloudAccounts] = useState<
    CloudAccountType[]
  >([]);
  const [selectedCloudAccountValue, setSelectedCloudAccountValue] =
    useState(null);
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccountType[]>([]);
  const [filteredCloudAccounts, setFilteredCloudAccounts] = useState<
    CloudAccountType[]
  >([]);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [selectApp, setSelectApp] = useState<boolean>(false);
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);

  useEffect(() => {
    const uniquePlatforms = [
      ...new Set(props?.userAccounts?.map(account => account?.platform)),
    ];
    setAccountTypes(uniquePlatforms?.sort());

    if (props?.appId) {
      setApplicationId(props.appId);
    } else {
      setSelectApp(true);
    }
  }, [props.appId, props.userAccounts]);

  useEffect(() => {
    if (props?.preSelectedAccounts) {
      setSelectedCloudAccounts(props.preSelectedAccounts ?? []);
    }
  }, [props.preSelectedAccounts]);

  useEffect(() => {
    const filteredAccounts = cloudAccounts.filter(
      account => account.applications && account.applications.length === 0,
    );
    setFilteredCloudAccounts(filteredAccounts);
  }, [cloudAccounts]);

  const onSelect = (_: any, value: any | null) => {
    if (!value) {
      return;
    }

    const selectedAccounts = props?.userAccounts?.filter(
      account => account?.platform === value,
    );
    setCloudAccounts(selectedAccounts);
  };

  const handleRemoveItem = (itemToRemove: any) => {
    const updatedItems = [...selectedCloudAccounts];
    const indexToRemove = updatedItems?.findIndex(
      item => item === itemToRemove,
    );
    if (indexToRemove !== -1) {
      updatedItems?.splice(indexToRemove, 1);

      setSelectedCloudAccounts(updatedItems);
    }
  };

  const onSelectCloudAccount = (_: any, value: any | null) => {
    setSelectedCloudAccountValue(value);

    if (
      !value ||
      selectedCloudAccounts?.some(account => account.id === value.id)
    ) {
      return;
    }

    setSelectedCloudAccounts([...selectedCloudAccounts, value]);
  };

  const onSelectApplication = (_: any, value: any | null) => {
    if (value?.applicationId) {
      setApplicationId(value.applicationId);
    } else {
      setApplicationId(null);
    }
  };

  const [createRelationship, { data, loading, error }] = useMutation(
    CreateCloudAccountMutation,
    {
      refetchQueries: [
        {
          query: GetApplicationQuery,
          variables: applicationVariables,
        },
        'GetApplicationDetails',
      ],
    },
  );

  const handleCloseModal = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (selectApp) {
      setApplicationId(null);
    }

    setSelectedAccountType('');
    setSelectedCloudAccounts([]);
    setSelectedCloudAccountValue(null);
    setSubmitted(false);
    props.handleClose(event);
  };

  const handleSubmit = async () => {
    const input = {
      applicationId: applicationId,
      cloudAccountIds: selectedCloudAccounts?.map(account => account.id),
    };

    setSubmitted(true);
    const createMutation = await createRelationship({
      variables: { input: input },
    });

    if (
      createMutation?.data?.devhub?.connections?.cloudAccount
        ?.addApplicationConnection?.success
    ) {
      if (props?.appId) {
        props.setCloudAccountsList([
          ...props.cloudAccountsList,
          ...selectedCloudAccounts,
        ]);
      } else {
        props.setCloudAccountsList(prevCloudAccountsList => {
          const updatedCloudAccountsList = prevCloudAccountsList.map(
            cloudAccount => {
              if (selectedCloudAccounts.includes(cloudAccount)) {
                const applicationToAdd = props.applications?.find(
                  app => app.applicationId === applicationId,
                );

                return {
                  ...cloudAccount,
                  applications: [
                    ...(cloudAccount?.applications || []),
                    applicationToAdd,
                  ] as ApplicationType[],
                };
              }
              return cloudAccount;
            },
          );

          return updatedCloudAccountsList as CloudAccountType[];
        });
      }
    }
  };

  const validate = () => {
    if (selectApp) {
      return selectedCloudAccounts.length === 0 || applicationId === null;
    }
    return selectedCloudAccounts.length === 0;
  };

  return (
    <>
      <DevHubBox>
        {props.type === AssociateAccountType.BUTTON && (
          <DevHubButton
            variant="contained"
            color="primary"
            height="large"
            onClick={props.handleOpen}
            disabled={props.disabled}
          >
            {props.buttonText}
          </DevHubButton>
        )}

        {props.type === AssociateAccountType.CARD && (
          <DevHubCard
            disabled={props.disabled}
            density="compact"
            consistentHeight
            variant="primary"
            cardButtonPosition="bottom"
            cardButton={
              <DevHubTooltip title={props.buttonText}>
                <span>
                  <DevHubButton
                    fullWidth
                    buttonColor="white"
                    textColor="black"
                    height="small"
                    variant="contained"
                    onClick={props.handleOpen}
                    disabled={props.disabled}
                  >
                    Associate a new cloud account
                  </DevHubButton>
                </span>
              </DevHubTooltip>
            }
            body={
              <DevHubTypography
                variant="subtitle1"
                align="center"
                fontColor="white"
              >
                Don't see a cloud account that should be tied to this
                application?
              </DevHubTypography>
            }
          />
        )}
      </DevHubBox>
      <DevHubModal
        open={props.open}
        onClose={handleCloseModal}
        title="Associate Cloud Account"
      >
        {submitted ? (
          <Submitted
            error={
              !data?.devhub?.connections?.cloudAccount?.addApplicationConnection
                ?.success || error
            }
            submitted={submitted}
            loading={loading}
            handleClose={handleCloseModal}
            success={
              data?.devhub?.connections?.cloudAccount?.addApplicationConnection
                ?.success
            }
            errorMessage={
              data?.devhub?.connections?.cloudAccount?.addApplicationConnection
                ?.message
            }
          />
        ) : (
          <>
            {accountTypes.length === 0 && (
              <DevHubTypography>
                You don't have any cloud accounts associated to your login.
              </DevHubTypography>
            )}
            {props?.applications && props?.applications?.length === 0 && (
              <DevHubTypography>
                You don't have any non-retired applications with roles of
                (approver, owner or owner assistant). <br />
                If you need access to an application please reach out to the
                application owner for assistance.
              </DevHubTypography>
            )}
            {selectApp &&
              props?.applications &&
              props?.applications?.length > 0 && (
                <DevHubAutocomplete
                  loading={loading}
                  loadingText="Loading.."
                  options={props.applications!}
                  onChange={onSelectApplication}
                  getOptionLabel={option => option.name}
                  renderInput={params => (
                    <DevHubTextField
                      {...params}
                      label="Select an application"
                      margin="normal"
                      helperText="Non-retired Application Selection"
                      variant="outlined"
                      required
                      InputProps={params.InputProps}
                    />
                  )}
                />
              )}
            {accountTypes.length > 0 && (
              <DevHubAutocomplete
                loading={loading}
                loadingText="Loading.."
                options={accountTypes}
                onChange={onSelect}
                getOptionLabel={option => option}
                renderInput={params => (
                  <DevHubTextField
                    {...params}
                    label="Search for a cloud account platform"
                    margin="normal"
                    helperText="Cloud Account Platform"
                    variant="outlined"
                    required
                    InputProps={params.InputProps}
                  />
                )}
              />
            )}

            {cloudAccounts.length > 0 && (
              <DevHubAutocomplete
                loading={loading}
                loadingText="Loading.."
                options={filteredCloudAccounts}
                value={selectedCloudAccountValue}
                onChange={onSelectCloudAccount}
                getOptionLabel={option => option.name}
                renderInput={params => (
                  <DevHubTextField
                    {...params}
                    label="Select a cloud account"
                    margin="normal"
                    helperText="Cloud Accounts that are not associated with any application"
                    variant="outlined"
                    required
                    InputProps={params.InputProps}
                  />
                )}
              />
            )}

            {(selectedCloudAccounts?.length ?? 0) > 0 &&
              selectedCloudAccounts.map((option, index) => (
                <DevHubChip
                  key={index}
                  label={option?.name}
                  onDelete={() => handleRemoveItem(option)}
                />
              ))}

            <DevHubBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <DevHubButton
                variant="contained"
                height="medium"
                color="primary"
                onClick={handleCloseModal}
              >
                Cancel
              </DevHubButton>
              {accountTypes.length > 0 && (
                <DevHubButton
                  variant="contained"
                  height="medium"
                  color="primary"
                  disabled={validate()}
                  onClick={handleSubmit}
                >
                  Link Selected Accounts
                </DevHubButton>
              )}
            </DevHubBox>
          </>
        )}
      </DevHubModal>
    </>
  );
}
